// @flow

import React, {Component} from 'react';
import Button from '@react/react-spectrum/Button';
import Link from '@react/react-spectrum/Link';
import AppPickerUtil from '../../../../../utils/AppPicker/AppPickerUtil';
import {
	DOWNLOAD_CONTROLLER_USER_SOURCE,
	LOGGED_OUT_CAMPAIGN_ENV_NAME
} from '../../../../../constants/AppPicker/appPickerConstants';
import AppsUtil from '../../../../../utils/AppsUtil';
import UrlUtil from '../../../../../utils/Url';
import CompatibilityUtil from '../../../../../utils/CompatibilityUtil';
import {getHeadingTextId, getBodyTextId, WORKFLOWS} from './../stringUtil';
import AppPickerAnalytics from '../../../../../analytics/AppPickerAnalytics';
import IncompatibleViewScss from './IncompatibleView.scss';
import uuid from 'uuid';
import CCDUtil from '../../../../../utils/CCDUtil';

const ANALYTICS = {
	CLICK_SYS_REQT: 'click-sys-reqt',
	CC_CTA_CLICK: 'cc-cta-click'
};

type Props = {
	className: ?string,
	productId: string,
	source: string, /* Place from where the Incompatible View is being used */
	locale: string,
	sapCode: string,
	environment: string,
	onGetHelp: Function,
	formatMessage: Function,
	hasThor: ?boolean,
	history: Object
};

type AnalyticObjectInterface = {
	event: {
		type?: string,
		subtype?: string,
		guid?: string,
		subcategory?: string,
		workflow?: string
	},
	ui?: {
		view_type: ?string
	},
	content?: {
		action?: string
	},
	env?: {
		name?: string,
		version?: string
	}
}

export default class IncompatibleView extends Component<Props> {
	onSystemReqClick = (e: Object) => {
		const {productId, sapCode, locale} = this.props;
		e.preventDefault();
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(
			AppPickerUtil.getSystemRequirementLink(sapCode) || AppsUtil.getCCDGoURL(locale, AppsUtil.getSystemReqURL(productId)),
			sapCode,
			guid
		);
		this.handleAnalyticsEvent(ANALYTICS.CLICK_SYS_REQT, {guid, url});
	}

	downloadCreativeCloud = (planName: ?string) => {
		const {hasThor, history} = this.props;
		const sapCode = 'KCCC';
		const productConfig = AppsUtil.getFFCProductConfigBySapCode(sapCode);
		this.handleAnalyticsEvent(ANALYTICS.CC_CTA_CLICK, {ctaType: hasThor ? 'open-ccd' : 'install-ccd'});
		if (productConfig) {
			const url = AppsUtil.getInstallationModalUrl({
				sapCode,
				plan: planName,
				platform: AppsUtil.getProductPlatform(productConfig)
			},
			{
				isCategoryPath: true,
				category: 'all',
				subCategory: 'desktop',
				isStarter: true
			});
			if (AppsUtil.hasFeature('temp_cch_10855')) {
				window._uxa = window._uxa || [];
				window._uxa.push(['trackPageview', url]);
			}
			history.push(url);
		}
	};

	redirectToCreativeCloudWeb = () => {
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(window.location.origin, undefined, guid, undefined, undefined, '_self');
		this.handleAnalyticsEvent(ANALYTICS.CC_CTA_CLICK, {ctaType: 'continue-to-creative-cloud', url, guid});
	}

	handleAnalyticsEvent(eventType: string, params: Object) {
		const {source, environment} = this.props;
		const analytics = new AppPickerAnalytics(environment);
		analytics.initAnalytics();
		let eventObj: AnalyticObjectInterface = {
			event: {}
		};
		switch (eventType) {
			case ANALYTICS.CLICK_SYS_REQT:
				eventObj = {
					event: {
						type: 'click',
						subtype: (source === DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE) ? 'learn-more' : 'view-system-requirements',
						guid: params.guid
					},
					content: {
						action: params.url
					},
					ui: {
						view_type: analytics.getPageTypeString() // eslint-disable-line camelcase
					}
				};
				break;
			case ANALYTICS.CC_CTA_CLICK:
				eventObj = {
					event: {
						type: 'click',
						subtype: params.ctaType,
						guid: params.guid
					},
					content: {
						action: params.url
					},
					ui: {
						view_type: analytics.getPageTypeString() // eslint-disable-line camelcase
					}
				};
				break;
		}
		if (source === DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE) {
			eventObj.event.subcategory = CCDUtil.getCampaignWorkflowSubcategory();
			eventObj.event.workflow = 'Campaign CTA';
			eventObj.ui = {
				view_type: 'Email Campaign' // eslint-disable-line camelcase
			};
			eventObj.env = {
				name: LOGGED_OUT_CAMPAIGN_ENV_NAME,
				version: UrlUtil.getParam('exp') || undefined
			};
		}
		else {
			eventObj.event.subcategory = 'Download';
		}
		analytics.genericAnalyticEvent(eventObj);
	}

	getDisplayContentObject() {
		const {formatMessage, productId, sapCode, source, hasThor} = this.props;
		const displayName = AppsUtil.getProductDisplayName(productId) || AppPickerUtil.getDisplayName(sapCode);
		const displayContent = {
			displayName,
			icon: AppsUtil.getProductIcon(AppsUtil.getFFCProductConfigBySapCode(sapCode)) || AppPickerUtil.getProductIcon(sapCode),
			showIcon: true,
			heading: formatMessage({id: getHeadingTextId(WORKFLOWS.INCOMPATIBLE, source, sapCode)}, {appName : displayName, userName : AppsUtil.getImsData().name}),
			subHeading: formatMessage(
				{id: getBodyTextId(WORKFLOWS.INCOMPATIBLE, source, sapCode, hasThor)},
				{appName : displayName}
			)
		};
		return displayContent;
	}

	renderHelpSection() {
		const {formatMessage, source, hasThor} = this.props;
		if (source === DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE) {
			return (<span>
				{formatMessage({id: 'apps.loggedOutCampaign.incompatible.helpSection'}, {
					a1: str => <Link onClick={e => this.onSystemReqClick(e)}>{str}</Link>,
					a2: str => <Link onClick={e => this.props.onGetHelp(e)}>{str}</Link>
				})}
			</span>);
		}
		else if (source === DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE) {
			let ccButtonTextId = 'apps.download.incompatible.dnldCcdCta';
			let ccButtonClickCallback = this.downloadCreativeCloud.bind(this, AppsUtil.getSingleAppPostPurchasePlan());
			if (!CompatibilityUtil.isAppSupported('KCCC')) {
				ccButtonTextId = 'apps.download.incompatible.continueToCCWebCTA';
				ccButtonClickCallback = this.redirectToCreativeCloudWeb;
			}
			else if (hasThor) {
				if (CompatibilityUtil.isCCDSupported()) {
					ccButtonTextId = 'apps.download.incompatible.openCcdCta';
				}
				else {
					ccButtonTextId = 'apps.download.incompatible.continueToCCWebCTA';
					ccButtonClickCallback = this.redirectToCreativeCloudWeb;
				}
			}
			return (<div className={IncompatibleViewScss.buttons}>
				<Button
					className={IncompatibleViewScss.ccCta}
					variant='cta'
					onClick={ccButtonClickCallback}
					disabled={!AppsUtil.getFFCProductConfigBySapCode('KCCC')}
					label={formatMessage({id: ccButtonTextId})}
				/>
				<Button
					className={IncompatibleViewScss.sysReqtCta}
					variant='primary'
					quiet
					onClick={this.onSystemReqClick}
					label={formatMessage({id: 'apps.download.incompatible.sysReqtsCta'})}
				/>
			</div>);
		}
	}

	render() {
		const {className} = this.props;
		const content = this.getDisplayContentObject();
		return (
			<div className={`${IncompatibleViewScss.incompatibleView} ${className || ''}`}>
				{content.icon ? (<img alt={content.displayName} src={content.icon} />) : []}
				<div data-cs-mask='' className={IncompatibleViewScss.incompatibleHeader}>{content.heading}</div>
				<div className={IncompatibleViewScss.incompatibleDesc}>{content.subHeading}</div>
				<div className={IncompatibleViewScss.helpSection}>
					{this.renderHelpSection()}
				</div>
			</div>
		);
	}
}
